import { useEffect, useCallback, type RefObject } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { api } from '@/lib/api';

/**
 * Folder focus-management effects + folder (place_list module) CRUD callbacks.
 * Refs are passed in (not recreated) to preserve identity.
 */
export function usePlaceFolders(params: {
  renamingFolderId: string | null;
  renameFolderInputRef: RefObject<HTMLInputElement | null>;
  isCreatingFolder: boolean;
  newFolderInputRef: RefObject<HTMLInputElement | null>;
  newFolderName: string;
  token: string | null;
  placesWorkspaceId: string;
  queryClient: QueryClient;
  setIsCreatingFolder: (v: boolean) => void;
  setNewFolderName: (v: string) => void;
  setFolderMenu: (v: { id: string; name: string; x: number; y: number } | null) => void;
  setRenamingFolderId: (v: string | null) => void;
}) {
  const {
    renamingFolderId,
    renameFolderInputRef,
    isCreatingFolder,
    newFolderInputRef,
    newFolderName,
    token,
    placesWorkspaceId,
    queryClient,
    setIsCreatingFolder,
    setNewFolderName,
    setFolderMenu,
    setRenamingFolderId,
  } = params;

  useEffect(() => {
    if (renamingFolderId && renameFolderInputRef.current) {
      renameFolderInputRef.current.focus();
      renameFolderInputRef.current.select();
    }
  }, [renamingFolderId]);

  useEffect(() => {
    if (isCreatingFolder && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
    }
  }, [isCreatingFolder]);

  const handleCreateFolder = useCallback(async () => {
    const name = newFolderName.trim();
    if (!name || !token || !placesWorkspaceId) return;

    try {
      await api.workspaces.createWorkspaceModule(token, placesWorkspaceId, {
        type: 'place_list',
        label: name,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(placesWorkspaceId) });
    } catch (e) {
      console.warn('[Places] Failed to create folder', e);
    }
    setIsCreatingFolder(false);
    setNewFolderName('');
  }, [newFolderName, token, placesWorkspaceId, queryClient]);

  const handleArchiveFolder = useCallback(async (folderId: string) => {
    try {
      await api.workspaces.updateWorkspaceModule(token ?? null, placesWorkspaceId, folderId, { archived: true } as any);
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(placesWorkspaceId) });
    } catch (e) {
      console.warn('[Places] Failed to archive folder', e);
    }
    setFolderMenu(null);
  }, [token, placesWorkspaceId, queryClient]);

  const handleRenameFolder = useCallback(async (folderId: string, newLabel: string) => {
    const label = newLabel.trim();
    if (!label) { setRenamingFolderId(null); return; }
    try {
      await api.workspaces.updateWorkspaceModule(token ?? null, placesWorkspaceId, folderId, { label });
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(placesWorkspaceId) });
    } catch (e) {
      console.warn('[Places] Failed to rename folder', e);
    }
    setRenamingFolderId(null);
  }, [token, placesWorkspaceId, queryClient]);

  return { handleCreateFolder, handleArchiveFolder, handleRenameFolder };
}
